import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Phone,
  ArrowRight,
  Layers,
  ShieldCheck,
  Building2,
  Wrench,
  Package,
  Layers3,
} from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import ProductsPage from "@/pages/ProductsPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PHONE_DISPLAY = "1 (800) 955-5780";
const PHONE_HREF = "tel:+18009555780";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=1800&q=80";

const PRODUCTS = [
  {
    slug: "epdm",
    icon: Layers,
    title: "EPDM Roofing Membranes",
    products: "Sure-Seal® EPDM · Sure-White® EPDM · FleeceBACK® EPDM",
    blurb:
      "Time-tested EPDM rubber membranes for flat and low-slope commercial roofs, known for long-term weathering resistance and proven performance.",
    image:
      "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=800&q=80",
  },
  {
    slug: "tpo",
    icon: ShieldCheck,
    title: "TPO Roofing Membranes",
    products: "Sure-Weld® TPO",
    blurb:
      "Heat-weldable TPO membranes delivering strong, consistent seams and energy-efficient reflective surfaces for commercial buildings.",
    image:
      "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=800&q=80",
  },
  {
    slug: "pvc",
    icon: Building2,
    title: "PVC Roofing Membranes",
    products: "Sure-Flex™ PVC",
    blurb:
      "Durable PVC membranes well suited for facilities exposed to chemicals, grease, or harsh rooftop conditions, including restaurants and industrial sites.",
    image:
      "https://images.unsplash.com/photo-1487958449943-2429e8be8625?auto=format&fit=crop&w=800&q=80",
  },
  {
    slug: "fleece",
    icon: Layers3,
    title: "Fleece-Backed Roofing Systems",
    products: "FleeceBACK® EPDM · FleeceBACK® TPO · FleeceBACK® PVC",
    blurb:
      "Carlisle's fleece-backed membrane systems for adhered installations, adding toughness and durability to EPDM, TPO, and PVC assemblies.",
    image:
      "https://images.unsplash.com/photo-1449157291145-7efd050a4d0e?auto=format&fit=crop&w=800&q=80",
  },
  {
    slug: "insulation",
    icon: Package,
    title: "Roof Insulation & Cover Boards",
    products: "InsulBase® Polyiso · SecurShield® Cover Boards",
    blurb:
      "Polyisocyanurate insulation and high-density cover boards that complete the roofing assembly with thermal performance and impact resistance.",
    image:
      "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=800&q=80",
  },
  {
    slug: "accessories",
    icon: Wrench,
    title: "Roofing Accessories",
    products: "Adhesives · Primers · Fasteners · Flashing",
    blurb:
      "System-matched adhesives, primers, fasteners, plates, flashing, and seam components that keep Carlisle SynTec installations watertight.",
    image:
      "https://images.unsplash.com/photo-1581094794329-c8112a89af12?auto=format&fit=crop&w=800&q=80",
  },
];

const MANUFACTURERS = [
  "GAF",
  "Johns Manville",
  "Elevate",
  "Sika Sarnafil",
  "Versico",
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Hero = () => (
  <section data-testid="hero-section" className="relative bg-navy-900">
    <div className="absolute inset-0">
      <img
        src={HERO_IMAGE}
        alt="Commercial roofing installation"
        className="h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-navy-950/80" />
    </div>
    <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 sm:py-32 lg:py-40">
      <p
        data-testid="hero-eyebrow"
        className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-steel-300 sm:text-sm"
      >
        Quadraciti · Commercial Roofing Supply
      </p>
      <h1
        data-testid="hero-headline"
        className="font-display max-w-3xl text-4xl font-extrabold leading-tight text-white sm:text-5xl lg:text-6xl"
      >
        Carlisle SynTec Roofing Products
      </h1>
      <p
        data-testid="hero-subtitle"
        className="mt-6 max-w-2xl text-base text-navy-100 sm:text-lg"
      >
        Explore high-performance roofing membranes and commercial roofing
        systems for flat roofs, industrial facilities, and commercial
        buildings.
      </p>
      <div className="mt-10 flex flex-col gap-4 sm:flex-row">
        <Button
          data-testid="hero-explore-products-button"
          size="lg"
          onClick={() => scrollTo("products")}
          className="bg-white px-8 font-bold text-navy-900 hover:bg-navy-100"
        >
          Explore Products <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
        <Button
          data-testid="hero-request-quote-button"
          size="lg"
          variant="outline"
          onClick={() => scrollTo("quote")}
          className="border-2 border-white bg-transparent px-8 font-bold text-white hover:bg-white hover:text-navy-900"
        >
          Request a Quote
        </Button>
      </div>
      <div className="mt-14 grid max-w-3xl grid-cols-1 gap-6 border-t border-navy-700 pt-8 sm:grid-cols-3">
        {[
          { title: "One location", sub: "Focused, personal service" },
          { title: "Leading manufacturers", sub: "Carlisle SynTec and more" },
          { title: PHONE_DISPLAY, sub: "Talk to our team directly" },
        ].map((f, i) => (
          <div key={i} data-testid={`hero-fact-${i}`}>
            <p className="font-display text-lg font-bold text-white">{f.title}</p>
            <p className="text-sm text-navy-200">{f.sub}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const ProductCard = ({ product, onView }) => {
  const Icon = product.icon;
  return (
    <Card
      data-testid={`product-card-${product.slug}`}
      className="group flex flex-col overflow-hidden border-gray-200 bg-white transition-shadow hover:shadow-xl"
    >
      <div className="relative h-44 overflow-hidden">
        <img
          src={product.image}
          alt={product.title}
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        <div className="absolute left-4 top-4 rounded-md bg-navy-900 p-2">
          <Icon className="h-5 w-5 text-white" />
        </div>
      </div>
      <CardContent className="flex flex-1 flex-col p-6">
        <h3 className="font-display text-xl font-bold text-navy-900">
          {product.title}
        </h3>
        <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-steel-500">
          {product.products}
        </p>
        <p className="mt-3 flex-1 text-sm leading-relaxed text-gray-600">
          {product.blurb}
        </p>
        <Button
          data-testid={`view-product-${product.slug}`}
          variant="outline"
          onClick={() => onView(product.title)}
          className="mt-5 border-navy-800 font-semibold text-navy-800 hover:bg-navy-800 hover:text-white"
        >
          View Product <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  );
};

const ProductsSection = ({ onView, onViewAll }) => (
  <section id="products" data-testid="products-section" className="bg-gray-100 py-20 sm:py-24">
    <div className="mx-auto max-w-7xl px-4 sm:px-6">
      <p className="text-xs font-bold uppercase tracking-[0.25em] text-steel-500">
        Featured Carlisle SynTec Categories
      </p>
      <h2 className="font-display mt-3 text-3xl font-extrabold text-navy-900 sm:text-4xl">
        Commercial Roofing Systems &amp; Components
      </h2>
      <p className="mt-4 max-w-2xl text-base text-gray-600">
        From single-ply membranes to insulation and accessories, Quadraciti
        supplies the core Carlisle SynTec product categories for commercial and
        industrial roofing projects.
      </p>
      <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {PRODUCTS.map((p) => (
          <ProductCard key={p.slug} product={p} onView={onView} />
        ))}
      </div>
      <div className="mt-12 text-center">
        <Button
          data-testid="view-all-products-button"
          size="lg"
          onClick={onViewAll}
          className="bg-navy-800 px-8 font-bold text-white hover:bg-navy-700"
        >
          View All 30 Roofing Products <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  </section>
);

const ManufacturersSection = () => (
  <section
    id="manufacturers"
    data-testid="manufacturers-section"
    className="bg-white py-16 sm:py-20"
  >
    <div className="mx-auto max-w-7xl px-4 sm:px-6">
      <h2 className="font-display text-2xl font-extrabold text-navy-900 sm:text-3xl">
        More Manufacturers We Supply
      </h2>
      <p className="mt-3 max-w-2xl text-base text-gray-600">
        Alongside Carlisle SynTec, we source commercial roofing products from
        other leading manufacturers.
      </p>
      <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {MANUFACTURERS.map((name) => (
          <div
            key={name}
            data-testid={`manufacturer-${name.toLowerCase().replace(/\s+/g, "-")}`}
            className="flex h-20 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 px-4"
          >
            <span className="font-display text-center text-lg font-bold text-navy-700">
              {name}
            </span>
          </div>
        ))}
      </div>
      <p className="mt-6 text-xs text-gray-500">
        All manufacturer names and trademarks are the property of their
        respective owners.
      </p>
    </div>
  </section>
);

const QuoteSection = ({ interest, setInterest }) => {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, {
        ...form,
        product_interest: interest || "General",
      });
      toast.success("Quote request received", {
        description: `Thank you, ${form.name}. Our team will reach out shortly. For immediate help, call ${PHONE_DISPLAY}.`,
      });
      setForm({ name: "", company: "", email: "", phone: "", message: "" });
      setInterest("");
    } catch (err) {
      toast.error("Could not send your request", {
        description: `Please try again or call us at ${PHONE_DISPLAY}.`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="bg-navy-900 py-20 sm:py-24">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 sm:px-6 lg:grid-cols-2">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-steel-300">
            Request a Quote
          </p>
          <h2 className="font-display mt-3 text-3xl font-extrabold text-white sm:text-4xl">
            Get Pricing on Carlisle SynTec Products
          </h2>
          <p className="mt-4 max-w-lg text-base text-navy-100">
            Tell us about your project and the products you need. Our team will
            prepare a quote for your commercial roofing job — membranes,
            insulation, accessories, and more.
          </p>
          <div className="mt-8 rounded-lg border border-navy-700 bg-navy-800 p-6">
            <p className="text-sm font-semibold text-navy-200">
              Prefer to talk it through?
            </p>
            <a
              data-testid="quote-phone-link"
              href={PHONE_HREF}
              className="font-display mt-2 flex items-center gap-2 text-2xl font-extrabold text-white hover:text-steel-300"
            >
              <Phone className="h-6 w-6" /> {PHONE_DISPLAY}
            </a>
            <p className="mt-2 text-sm text-navy-200">
              One location, one team — you will always reach someone who knows
              the products.
            </p>
          </div>
        </div>
        <form
          data-testid="quote-form"
          onSubmit={submit}
          className="rounded-xl bg-white p-6 shadow-2xl sm:p-8"
        >
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <Label htmlFor="q-name">Name *</Label>
              <Input
                id="q-name"
                data-testid="quote-name-input"
                required
                value={form.name}
                onChange={update("name")}
                className="mt-1.5"
                placeholder="Your name"
              />
            </div>
            <div>
              <Label htmlFor="q-company">Company</Label>
              <Input
                id="q-company"
                data-testid="quote-company-input"
                value={form.company}
                onChange={update("company")}
                className="mt-1.5"
                placeholder="Company name"
              />
            </div>
            <div>
              <Label htmlFor="q-email">Email *</Label>
              <Input
                id="q-email"
                data-testid="quote-email-input"
                type="email"
                required
                value={form.email}
                onChange={update("email")}
                className="mt-1.5"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <Label htmlFor="q-phone">Phone</Label>
              <Input
                id="q-phone"
                data-testid="quote-phone-input"
                type="tel"
                value={form.phone}
                onChange={update("phone")}
                className="mt-1.5"
                placeholder="(555) 000-0000"
              />
            </div>
            <div className="sm:col-span-2">
              <Label>Product Interest</Label>
              <Select value={interest} onValueChange={setInterest}>
                <SelectTrigger data-testid="quote-product-select" className="mt-1.5">
                  <SelectValue placeholder="Select a product category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem data-testid="quote-product-general" value="General">
                    General / Not sure yet
                  </SelectItem>
                  {PRODUCTS.map((p) => (
                    <SelectItem
                      key={p.slug}
                      data-testid={`quote-product-${p.slug}`}
                      value={p.title}
                    >
                      {p.title}
                    </SelectItem>
                  ))}
                  {interest &&
                    interest !== "General" &&
                    !PRODUCTS.some((p) => p.title === interest) && (
                      <SelectItem data-testid="quote-product-custom" value={interest}>
                        {interest}
                      </SelectItem>
                    )}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="q-message">Project Details</Label>
              <Textarea
                id="q-message"
                data-testid="quote-message-input"
                value={form.message}
                onChange={update("message")}
                className="mt-1.5 min-h-[110px]"
                placeholder="Roof size, timeline, products needed..."
              />
            </div>
          </div>
          <Button
            data-testid="quote-submit-button"
            type="submit"
            disabled={submitting}
            className="mt-6 w-full bg-navy-800 py-6 text-base font-bold text-white hover:bg-navy-700"
          >
            {submitting ? "Sending..." : "Submit Quote Request"}
          </Button>
        </form>
      </div>
    </section>
  );
};

const HOME_TO_CATEGORY = {
  "EPDM Roofing Membranes": "epdm",
  "TPO Roofing Membranes": "tpo",
  "PVC Roofing Membranes": "pvc",
  "Fleece-Backed Roofing Systems": "fleece",
  "Roof Insulation & Cover Boards": "insulation",
  "Roofing Accessories": "accessories",
};

const Home = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [interest, setInterest] = useState(searchParams.get("interest") || "");

  useEffect(() => {
    if (searchParams.get("interest")) {
      const t = setTimeout(() => scrollTo("quote"), 400);
      return () => clearTimeout(t);
    }
  }, [searchParams]);

  const onViewProduct = (title) => {
    navigate(`/products#${HOME_TO_CATEGORY[title] || ""}`);
  };

  return (
    <div data-testid="home-page" className="min-h-screen bg-white">
      <SiteHeader />
      <Hero />
      <ProductsSection onView={onViewProduct} onViewAll={() => navigate("/products")} />
      <ManufacturersSection />
      <QuoteSection interest={interest} setInterest={setInterest} />
      <SiteFooter />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/products/:slug" element={<ProductDetailPage />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
